import { FC, useState } from 'react';
import DragDropVerticalIcon from '@/components/icons/DragDropVerticalIcon';
import { Button } from '@/components/ui/button';
import MoreHorizontalIcon from '@/components/icons/MoreHorizontalIcon';
import { FaqListItemResponseDto } from '@/features/faq/types/faq-dto';
import EDropDownMenu from '@/components/everridge/EDropDownMenu';
import { t } from 'i18next';
import ComposeIcon from '@/components/icons/ComposeIcon';
import Delete_02_Icon from '@/components/icons/Delete_02_Icon';
import { useFaqDetailsApi } from '@/features/faq/hooks/useFaqDetailsApi';
import EInfoModal from '@/components/everridge/EInfoModal';
import { cn } from '@/lib/utils';

type Props = {
	onClick?: (id: string) => void;
	onEdit?: (id: string) => void;
	onDragDrop: (id: string, newIndex: number) => void;
	isMobile?: boolean;
} & FaqListItemResponseDto;

const FaqAdminCard: FC<Props> = ({
	id,
	question,
	answer,
	linkUrl,
	orderIndex,
	onClick,
	onEdit,
	onDragDrop,
	isMobile,
}) => {
	const [showDeleteWarning, setShowDeleteWarning] = useState(false);
	const [isDragging, setIsDragging] = useState(false);

	const { deleteFaq } = useFaqDetailsApi();

	const handleDragStart = (e: React.DragEvent<HTMLElement>) => {
		if (!isDragging) {
			e.preventDefault();
			return;
		}
		setIsDragging(true);
		e.dataTransfer.setData('draggedId', id);
	};

	const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
		e.preventDefault();
	};

	const handleDragDrop = (e: React.DragEvent<HTMLElement>) => {
		e.preventDefault();
		const draggedId = e.dataTransfer.getData('draggedId');
		if (draggedId !== id) onDragDrop?.(id, orderIndex);
		setIsDragging(false);
	};

	return (
		<>
			<article
				draggable={true}
				data-id={id}
				onClick={() => onClick?.(id)}
				onDragStart={handleDragStart}
				onDragOver={handleDragOver}
				onDrop={handleDragDrop}
				onDragEnd={() => setIsDragging(false)}
				className={cn(
					'p-6 flex items-center gap-2 rounded-2.5xl bg-popover-10 select-none hover:shadow-custom',
					isMobile && 'p-4',
					isDragging
						? 'border-2 border-primary-color-1000'
						: 'border border-popover-100',
				)}>
				<Button
					variant='ghost'
					size='icon'
					className={cn('size-8 self-start', isMobile && 'size-5')}
					onMouseDown={() => setIsDragging(true)}
					onMouseUp={() => setIsDragging(false)}
					onMouseLeave={() => setIsDragging(false)}>
					<DragDropVerticalIcon />
				</Button>
				<div className='flex-1 flex flex-col gap-2'>
					<h4
						className={cn(
							'text-lg line-clamp-1',
							isMobile && 'text-sm',
						)}>
						{question}
					</h4>
					<div
						className={cn(
							'text-base select-none',
							isMobile && 'text-sm',
						)}>
						<span className='text-popover-800 line-clamp-1'>
							{answer}
						</span>
						{linkUrl && (
							<>
								&nbsp;&nbsp;
								<a
									href={linkUrl}
									target='_blank'>
									<span className='font-medium'>
										{t(
											'main.faq.components.card.learnMore',
										)}
									</span>
								</a>
							</>
						)}
					</div>
				</div>
				<EDropDownMenu
					icon={MoreHorizontalIcon}
					buttonVariant='ghost'
					buttonSize='icon'
					buttonClassName='size-8'
					containerClassName='min-w-fit'>
					<Button
						variant='ghost'
						className='justify-start rounded-none!'
						onClick={() => onEdit?.(id)}>
						<ComposeIcon />
						{t('elements.buttons.edit')}
					</Button>
					<Button
						variant='ghost'
						className='justify-start rounded-none! text-error-1000 active:bg-error-100'
						onClick={() => setShowDeleteWarning(true)}>
						<Delete_02_Icon className='stroke-error-1000' />
						{t('elements.buttons.delete')}
					</Button>
				</EDropDownMenu>
			</article>
			{showDeleteWarning && (
				<EInfoModal
					label={t('main.faq.components.discard.title')}
					description={t('main.faq.components.discard.description')}
					submitName={t('elements.buttons.delete')}
					onSubmit={() => deleteFaq(id)}
					closeName={t('elements.buttons.cancel')}
					onClose={() => setShowDeleteWarning(false)}
				/>
			)}
		</>
	);
};

export default FaqAdminCard;
